<script setup lang="ts">
import { availablePairsPerExchange } from '@/lib/external-data/availablePairsPerExchange'
import type { AvailablePairsPerExchange } from "@/lib/external-data/availablePairsPerExchange.ts"
import { reactive, ref, watch } from 'vue';
import type { CalculateRequest, CoinsPerExchange, UserConfig } from '@/lib/interfaces/calculator';
import { useDebounceFn, useLocalStorage } from '@vueuse/core';
import calculate from './lib/calculate';
import Results from './Results.vue';
import ExchangeInput from "./inputs/ExchangeInput.vue"
import CapitalInput from './inputs/CapitalInput.vue';
import SelectedCoinsInput from './inputs/SelectedCoinsInput/SelectedCoinsInput.vue';
import AutopilotInput from './inputs/AutopilotInput.vue';
import { useAsyncState } from '@vueuse/core'
import { defaults } from './lib/defaultConfig.ts';
import getDeepdiveUrl from '@/lib/getDeepdiveUrl.ts';
import type { ExchangeName } from '@/lib/interfaces/exchanges.ts';
import useAosRefresh from '@/composables/useAosRefresh.ts';

/**
 * 
 * due to Astro seperating different vue components into different vue instances, there
 * is no easy way to communicate between selected-exchange & calculator, especially when the flow can start from 
 * the home page and end at a different page, url params are janky.
 * 
 */

const selectedExchangeLocalStorage = useLocalStorage<ExchangeName>('selected-exchange', defaults.userConfig.exchange)

const userConfig = reactive<UserConfig>(defaults.userConfig)

const configReuseDelays = [
  0,
  0,
  0,
  0,
  0,
  0,
  0,
  0,
  200,
  400,
  600,
  800,
  1000,
  1200,
  1400,
  1600,
  1800,
  2000,
  3000,
  4000,
  5000,
  7000,
  8000,
  9000,
  10_000,
  10_200,
  10_400,
  10_600,
  10_800,
  11_000,
  11_200,
  11_400,
  11_600,
  11_800,
  12_000,
  13_000,
  20_000,
  22_000,
  24_000,
  26_000
] // in ms

let counter = 0

watch(selectedExchangeLocalStorage, exch => {
  userConfig.exchange = exch
})

watch(() => userConfig.exchange, exch => {
  selectedExchangeLocalStorage.value = exch
})

const coinsPerExchange = getCoinsPerExchange(availablePairsPerExchange.exchanges)

const hasUnattendedUserConfigChange = ref(false)
const { state: response, execute } = useAsyncState(getResults, undefined, { immediate: false })
const debouncedExecute = useDebounceFn(execute, 2000, { maxWait: 8000 })

watch(userConfig, async () => {

  // if a quote request is pending dont queue another quote request.
  if(hasUnattendedUserConfigChange.value) return

  hasUnattendedUserConfigChange.value = true
  debouncedExecute()
  
}, { deep: true, immediate: true })

async function getResults() {

  const autopilotParams: CalculateRequest['autopilot'] = {
    enable: userConfig.autopilot.enable,
    coins: userConfig.autopilot.coins === 'auto'
      ? 'auto' 
      : Number(userConfig.autopilot.coins)
  }

  // make each try take longer
  await new Promise(res => {

    if(counter > configReuseDelays.length) setTimeout(res, configReuseDelays[configReuseDelays.length - 1])
    else setTimeout(res, configReuseDelays[counter])

  })

  const res = await calculate({
    initialAmount: Number(userConfig.initialAmount),
    period: userConfig.period,
    assetsOn: userConfig.selectedCoins,
    userExchange: userConfig.exchange,
    autopilot: autopilotParams
  })

  hasUnattendedUserConfigChange.value = false

  counter++

  return res

}

function getCoinsPerExchange(exchanges: AvailablePairsPerExchange['exchanges']) {

  return Object.fromEntries(Object.entries(exchanges).map(([label, currencies]) => ([
    label,
    currencies.map(currency => {

      const currencyName = currency.split('/')[0] as string 
      
      return {
        label: currencyName,
        value: currencyName,
      }

    })
  ]))) as CoinsPerExchange

}

useAosRefresh()

</script>

<template>
  <div 
    id="calculator" 
    data-aos
    class="flex flex-col items-center duration-500 [&:not(.aos-animate)]:(opacity-0 translate-y-5) delay-200 relative z-2 isolate" 
  >
    <div 
      :class="[
        'w-full flex flex-col lt-sm:(px-2 pb-6 pt-2) sm:p-6 gap-6 bg-brand-background/90 relative',
        'border-brand-stroke/10 border-2 rounded-4',
        'lg:(flex-row)'
      ]"
    >
      <div 
        class="w-full lg:w-1/3 px-2 isolate"
      >
        <!-- Exchanges -->
        <ExchangeInput 
          v-model="userConfig.exchange"
        />
        <CapitalInput 
          v-model="userConfig.initialAmount"
        />
        <div class="w-full bg-brand-stroke/10 h-.5 my-7.5" />
        <!-- Coins -->
        <SelectedCoinsInput
          :availableCoins="coinsPerExchange[userConfig.exchange]"
          v-model:selectedCoins="userConfig.selectedCoins"
        />
        <AutopilotInput 
          :availableCoinsAmount="coinsPerExchange[userConfig.exchange].length"
          v-model:autopilot="userConfig.autopilot"
        />
      </div>
      <div :class="[
        'w-full pb-10 lg:pb-0 lg:order-none order-first lg:w-2/3 border-brand-primary/10 border-2 rounded-3 px-6 xl:px-20 overflow-hidden relative h-104'
      ]">
        <Results 
          :hasUnattendedUserConfigChange
          :response
          :userConfig
          v-model:period="userConfig.period"
        />
      </div>
    </div>
    <a 
      class="mt-5 flex items-center gap-x-1.5 -z-5"
      :href="getDeepdiveUrl('details/calculator/how-we-calculate-earnings')"
    >
      <span 
        class="text-sm font-semibold text-brand-secondary"
      >
        {{ $t('learn_how_we_calculate_your_earnings') }}
      </span>
      <span class="i-material-symbols:keyboard-arrow-down text-xl mt-.5 bg-white/80" />
    </a>
  </div>
</template>